import React, { useState } from 'react';
import './App.css';

const MIN_LATENCY_MS = 0.1;
const MAX_LATENCY_MS = 1000;

export function calcuateEffectiveBandwidth(
  tcpRmem: string,
  tcpWmem: string,
  rttLatencyMs: number,
  tcpAdvWinScale: string
): string {

  const [, , tcpRmemMax]= tcpRmem.trim().split(/\s+/)
  const [, , tcpWmemMax] = tcpWmem.trim().split(/\s+/)


  // Receive max
  const receiveMbps = calculateThroughputMbps(tcpRmemMax, rttLatencyMs, tcpAdvWinScale, false);

  // Write max
  const writeMbps = calculateThroughputMbps(tcpWmemMax, rttLatencyMs, tcpAdvWinScale, true);


  return "Receive: " + formatMbps(receiveMbps) + ' Mbps; Transmit: ' + formatMbps(writeMbps) + ' Mbps';
}

export function calculateThroughputMbps(maxBytes: string | number, latencyMs: number, tcpAdvWinScale: string, transmit: boolean): number {
  const maxBytesInt = typeof maxBytes === 'number' ? maxBytes : parseInt(maxBytes, 10);
  var bufferOverhead = 0;


  var bytesForTCPWindow : number
  // This setting doesn't matter for transmit
  if (transmit) {
    bytesForTCPWindow = maxBytesInt;
  } else {
    const tcpAdvWinScaleInt = parseInt(tcpAdvWinScale);
    if (tcpAdvWinScaleInt > 0) {
      bufferOverhead = maxBytesInt / ( 2 ** tcpAdvWinScaleInt);
    } else {
      bufferOverhead = maxBytesInt - (maxBytesInt/ ( 2 ** (-1 * tcpAdvWinScaleInt)));
    }
    bytesForTCPWindow = maxBytesInt - bufferOverhead;
  }

  const rttPerSecond = 1000 / latencyMs;
  const bytesPerSecond = bytesForTCPWindow * rttPerSecond;

  return (bytesPerSecond * 8) / 1024 / 1024;
}

export function formatMbps(mbps: number): string {
  if (mbps > 10) {
    return mbps.toFixed(0);
  }

  if (mbps >= 0.5) {
    return mbps.toFixed(1);
  }

  return mbps.toFixed(2);
}

export function calculcateEffectiveBandwidth(
  tcpRmem: string,
  tcpWmem: string,
  rttLatencyMs: number,
  tcpAdvWinScale: string
): string {
  return calcuateEffectiveBandwidth(
    tcpRmem,
    tcpWmem,
    rttLatencyMs,
    tcpAdvWinScale
  );
}

function toLogSliderValue(ms: number): number {
  const minLog = Math.log10(MIN_LATENCY_MS);
  const maxLog = Math.log10(MAX_LATENCY_MS);
  const clamped = Math.min(MAX_LATENCY_MS, Math.max(MIN_LATENCY_MS, ms));
  return ((Math.log10(clamped) - minLog) / (maxLog - minLog)) * 100;
}

function fromLogSliderValue(sliderValue: number): number {
  const minLog = Math.log10(MIN_LATENCY_MS);
  const maxLog = Math.log10(MAX_LATENCY_MS);
  const clamped = Math.min(100, Math.max(0, sliderValue));
  const exponent = minLog + ((maxLog - minLog) * clamped) / 100;
  return Number((10 ** exponent).toFixed(2));
}

function parseMaxBytes(tcpMem: string): number {
  const [, , maxBytes] = tcpMem.trim().split(/\s+/);

  return parseInt(maxBytes, 10);
}

type ThroughputGraphProps = {
  title: string;
  maxBytes: number;
  currentLatencyMs: number;
  tcpAdvWinScale: string;
  transmit: boolean;
};

function ThroughputGraph({ title, maxBytes, currentLatencyMs, tcpAdvWinScale, transmit }: ThroughputGraphProps) {
  const width = 520;
  const height = 280;
  const padding = {
    top: 18,
    right: 18,
    bottom: 44,
    left: 64,
  };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const sampleCount = 600;
  const tcpAdvWinScaleValue = parseInt(tcpAdvWinScale, 10);
  const hasValue =
    Number.isFinite(maxBytes) &&
    maxBytes > 0 &&
    (transmit || Number.isFinite(tcpAdvWinScaleValue));
  const minThroughput = hasValue
    ? calculateThroughputMbps(maxBytes, MAX_LATENCY_MS, tcpAdvWinScale, transmit)
    : 1;
  const maxThroughput = hasValue
    ? calculateThroughputMbps(maxBytes, MIN_LATENCY_MS, tcpAdvWinScale, transmit)
    : 1;
  const minThroughputLog = Math.log10(minThroughput);
  const maxThroughputLog = Math.log10(maxThroughput);
  const currentLatency = Math.min(
    MAX_LATENCY_MS,
    Math.max(MIN_LATENCY_MS, currentLatencyMs)
  );
  const currentThroughput = hasValue
    ? calculateThroughputMbps(maxBytes, currentLatency, tcpAdvWinScale, transmit)
    : 0;
  const iconTitle = transmit ? 'Transmit' : 'Receive';

  const xForLatency = (latencyMs: number): number =>
    padding.left +
    ((latencyMs - MIN_LATENCY_MS) / (MAX_LATENCY_MS - MIN_LATENCY_MS)) *
      chartWidth;

  const yForThroughput = (mbps: number): number => {
    if (!hasValue) {
      return padding.top + chartHeight;
    }

    const clampedMbps = Math.min(maxThroughput, Math.max(minThroughput, mbps));
    const logRatio =
      (Math.log10(clampedMbps) - minThroughputLog) /
      (maxThroughputLog - minThroughputLog);

    return padding.top + chartHeight - logRatio * chartHeight;
  };

  const linePath = Array.from({ length: sampleCount }, (_, index) => {
    const latency =
      MIN_LATENCY_MS +
      ((MAX_LATENCY_MS - MIN_LATENCY_MS) * index) / (sampleCount - 1);
    const throughput = hasValue ? calculateThroughputMbps(maxBytes, latency, tcpAdvWinScale, transmit) : 0;
    const command = index === 0 ? 'M' : 'L';

    return `${command} ${xForLatency(latency).toFixed(2)} ${yForThroughput(
      throughput
    ).toFixed(2)}`;
  }).join(' ');
  const currentX = xForLatency(currentLatency);
  const currentY = yForThroughput(currentThroughput);
  const xTicks = [0.1, 250, 500, 750, 1000];
  const yTickCount = 8;
  const yTicks = hasValue
    ? Array.from({ length: yTickCount }, (_, index) => {
        const logValue =
          minThroughputLog +
          ((maxThroughputLog - minThroughputLog) * index) /
            (yTickCount - 1);

        return 10 ** logValue;
      })
    : [1];

  return (
    <article className="graph-card">
      <div className="graph-result" aria-live="polite">
        <span className="graph-result-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path
              d={
                transmit
                  ? 'M12 19V5M12 5l-5 5M12 5l5 5'
                  : 'M12 5v14M12 19l-5-5M12 19l5-5'
              }
            />
          </svg>
        </span>
        <span>
          <span className="graph-result-label">{iconTitle}</span>
          <span className="graph-result-value">
            {formatMbps(currentThroughput)} Mbps
          </span>
        </span>
      </div>
      <svg
        className="throughput-graph"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${title} effective throughput by latency`}
      >
        <line
          className="axis"
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={height - padding.bottom}
        />
        <line
          className="axis"
          x1={padding.left}
          y1={height - padding.bottom}
          x2={width - padding.right}
          y2={height - padding.bottom}
        />

        {yTicks.map((tick) => {
          const y = yForThroughput(tick);

          return (
            <g key={tick}>
              <line
                className="grid-line"
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
              />
              <text className="tick-label" x={padding.left - 10} y={y + 4} textAnchor="end">
                {formatMbps(tick)}
              </text>
            </g>
          );
        })}

        {xTicks.map((tick) => {
          const x = xForLatency(tick);

          return (
            <g key={tick}>
              <line
                className="tick-line"
                x1={x}
                y1={height - padding.bottom}
                x2={x}
                y2={height - padding.bottom + 5}
              />
              <text className="tick-label" x={x} y={height - padding.bottom + 22} textAnchor="middle">
                {tick}
              </text>
            </g>
          );
        })}

        <path className="throughput-line" d={linePath} />
        <circle className="current-dot" cx={currentX} cy={currentY} r={5} />
        <text className="axis-label" x={padding.left + chartWidth / 2} y={height - 8} textAnchor="middle">
          Latency (ms)
        </text>
        <text
          className="axis-label"
          x={16}
          y={padding.top + chartHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90 16 ${padding.top + chartHeight / 2})`}
        >
          Mbps
        </text>
      </svg>
    </article>
  );
}

function App() {
  const [tcpRmem, setTcpRmem] = useState('');
  const [tcpWmem, setTcpWmem] = useState('');
  const [rttLatencyMs, setRttLatencyMs] = useState(10);
  const [tcpAdvWinScale, setTcpAdvWinScale] = useState('');
  const [isAdvWinScaleInfoOpen, setIsAdvWinScaleInfoOpen] = useState(false);
  const [isAdvWinScaleInfoHovered, setIsAdvWinScaleInfoHovered] = useState(false);
  const [suppressAdvWinScaleInfoHover, setSuppressAdvWinScaleInfoHover] = useState(false);

  const sliderValue = toLogSliderValue(rttLatencyMs);
  const receiveMaxBytes = parseMaxBytes(tcpRmem);
  const writeMaxBytes = parseMaxBytes(tcpWmem);
  const showAdvWinScaleInfo =
    isAdvWinScaleInfoOpen ||
    (isAdvWinScaleInfoHovered && !suppressAdvWinScaleInfoHover);

  return (
    <div className="app">
      <main className="panel">
        <h1>Maximum Usable TCP Bandwidth Calculator</h1>
        <p>The maximum possible throughput on a TCP connection is a function of the latency, and TCP window size.  On Linux,
          the TCP window size is constrained by the socket buffer size (set via kernel sysctl parameters).

          <br/><br/>

          This calculates the maximum bandwidth based on the sysctl settings and latency.  Actual throughput is likely to be lower,
          due to packet loss / congestion, the application itself not being able to send or receive data quickly enough, etc.

          <br/><br/>

          Both the send (WMEM) and receive (RMEM) socket buffer sizes include a minimum, default, and maximum value.  The kernel "autotunes"
          the actual value for each connection within this range.  This calculator uses only the maximum byte value.
        </p>
        <label htmlFor="tcp-rmem" className="input-label">
          sysctl TCP RMEM setting (<code>cat /proc/sys/net/ipv4/tcp_rmem</code>)
        </label>
        <textarea
          id="tcp-rmem"
          className="large-text"
          placeholder="Paste net.ipv4.tcp_rmem data here..."
          value={tcpRmem}
          onChange={(event) => {
            const nextTcpRmem = event.target.value;
            setTcpRmem(nextTcpRmem);
          }}
        />

        <label htmlFor="tcp-wmem" className="input-label">
          sysctl TCP WMEM setting (<code>cat /proc/sys/net/ipv4/tcp_wmem</code>)
        </label>
        <textarea
          id="tcp-wmem"
          className="large-text"
          placeholder="Paste net.ipv4.tcp_wmem data here..."
          value={tcpWmem}
          onChange={(event) => {
            const nextTcpWmem = event.target.value;
            setTcpWmem(nextTcpWmem);
          }}
        />

        <div className="row">
          <div className="field">
            <label htmlFor="rtt-slider" className="input-label">
              RTT latency (ms)
            </label>
            <input
              id="rtt-slider"
              type="range"
              min={0}
              max={100}
              step={1}
              value={sliderValue}
              onChange={(event) => {
                const nextRttLatencyMs = fromLogSliderValue(
                  Number(event.target.value)
                );
                setRttLatencyMs(nextRttLatencyMs);
              }}
            />
            <input
              className="small-input"
              type="number"
              min={0.1}
              max={1000}
              step={0.1}
              value={rttLatencyMs}
              onChange={(event) => {
                const nextValue = Number(event.target.value);
                if (Number.isNaN(nextValue)) {
                  return;
                }
                const nextRttLatencyMs = Math.min(1000, Math.max(0.1, nextValue));
                setRttLatencyMs(nextRttLatencyMs);
              }}
            />
            <p className="hint">Log scale: 0.1ms to 1000ms (default 10ms)</p>
          </div>

          <div className="field">
            <label htmlFor="tcp-adv-win-scale" className="input-label">
              sysctl tcp_adv_win_scale (<code>cat /proc/sys/net/ipv4/tcp_adv_win_scale</code>)
              <span
                className={`info-wrapper${showAdvWinScaleInfo ? ' is-open' : ''}`}
                onMouseEnter={() => {
                  setIsAdvWinScaleInfoHovered(true);
                }}
                onMouseLeave={() => {
                  setIsAdvWinScaleInfoHovered(false);
                  setSuppressAdvWinScaleInfoHover(false);
                }}
              >
                <button
                  type="button"
                  className="info-icon"
                  aria-label="More information about tcp_adv_win_scale"
                  aria-expanded={showAdvWinScaleInfo}
                  aria-describedby="tcp-adv-win-scale-info"
                  onClick={(event) => {
                    event.preventDefault();
                    setIsAdvWinScaleInfoOpen((isOpen) => {
                      const nextIsOpen = !isOpen;

                      setSuppressAdvWinScaleInfoHover(!nextIsOpen);

                      return nextIsOpen;
                    });
                  }}
                >
                  i
                </button>
                <span
                  id="tcp-adv-win-scale-info"
                  className="info-tooltip"
                  role="tooltip"
                >
                  The socket buffer includes more than just the TCP window size.  The kernel also reserves part of it for it's own purposes,
                  reducing the allowed memory used for the TCP window size.  This sysctl setting controls the ratio of how much of the socket
                  buffer is used for each purpose.  See <a href="https://man7.org/linux/man-pages/man7/tcp.7.html#:~:text=tcp%5Fadv%5Fwin%5Fscale" target="_blank" rel="noreferrer">this man page</a> for more information.  The kernel default on modern kernels is 2.
                </span>
              </span>
            </label>
            <input
              id="tcp-adv-win-scale"
              className="small-input"
              type="text"
              placeholder="e.g. 1"
              value={tcpAdvWinScale}
              onChange={(event) => {
                const nextTcpAdvWinScale = event.target.value;
                setTcpAdvWinScale(nextTcpAdvWinScale);
              }}
            />
          </div>
        </div>

        <section className="graphs" aria-label="Effective throughput graphs">
          <ThroughputGraph
            title="Receive"
            maxBytes={receiveMaxBytes}
            currentLatencyMs={rttLatencyMs}
            tcpAdvWinScale={tcpAdvWinScale}
            transmit={false}
          />
          <ThroughputGraph
            title="Transmit"
            maxBytes={writeMaxBytes}
            currentLatencyMs={rttLatencyMs}
            tcpAdvWinScale={tcpAdvWinScale}
            transmit={true}
          />
        </section>

        <p className="author-credit">Made by Oliver Garraux</p>
      </main>
    </div>
  );
}

export default App;
